import { useId, useState, type FormEvent } from "react";
import {
  ArrowRight,
  BookOpenText,
  CheckCircle2,
  LoaderCircle,
  Mail,
  PackageCheck,
  RefreshCw,
} from "lucide-react";
import { useSite } from "@/hooks/use-site";
import type { NewsletterSource } from "@/lib/newsletter-subscription";
import { localizeUrl } from "@/lib/site";
import { cn } from "@/lib/utils";

type NewsletterVariant = "home" | "blog" | "article" | "page";

export function NewsletterSignup({
  source,
  variant = "blog",
}: {
  source: NewsletterSource;
  variant?: NewsletterVariant;
}) {
  const { site, messages, href } = useSite();
  const inputId = useId();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const endpoint = localizeUrl("/api/subscribe", site.locale, href);
  const privacyHref = localizeUrl("/privacy", site.locale, href);
  const page = variant === "page";
  const dark = variant === "home" || page;
  const article = variant === "article";
  const Heading = page ? "h1" : "h2";
  const formTitleId = `${inputId}-form-title`;
  const pageBenefits = [
    { Icon: PackageCheck, label: messages.newsletterPageBenefitProducts },
    { Icon: RefreshCw, label: messages.newsletterPageBenefitFirmware },
    { Icon: BookOpenText, label: messages.newsletterPageBenefitInsights },
  ];

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("submitting");

    const data = new FormData(event.currentTarget);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, source, website: String(data.get("website") ?? "") }),
      });
      if (!response.ok) throw new Error("Subscription failed");
      setEmail("");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const panel = (
    <div
      className={cn(
        "relative overflow-hidden rounded-3xl border",
        dark
          ? "border-white/10 bg-slate-950 px-6 py-10 text-white shadow-2xl shadow-slate-950/15 sm:px-10 sm:py-12 lg:px-14"
          : "border-border bg-card px-6 py-8 shadow-[var(--shadow-elevated)] sm:px-8",
        page && "lg:px-16 lg:py-14",
        page &&
          "animate-in fade-in slide-in-from-bottom-4 fill-mode-both duration-700 motion-reduce:animate-none",
        article &&
          "mt-12 rounded-2xl border-border/80 bg-muted/25 px-6 py-8 shadow-sm sm:px-8 sm:py-9",
      )}
    >
      {dark && (
        <>
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 left-1/3 h-64 w-64 rounded-full bg-sky-400/10 blur-3xl" />
        </>
      )}
      <div
        className={cn(
          "relative z-10 grid items-center gap-7",
          article
            ? "gap-6"
            : page
              ? "lg:grid-cols-[minmax(0,1.05fr)_minmax(22rem,0.78fr)] lg:gap-16"
              : "lg:grid-cols-[1.1fr_1fr] lg:gap-12",
        )}
      >
        <div
          className={cn(
            page &&
              "animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-500 [animation-delay:100ms] motion-reduce:animate-none",
          )}
        >
          <p
            className={cn(
              "flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em]",
              dark ? "text-sky-300" : "text-primary",
            )}
          >
            <Mail aria-hidden className="h-4 w-4" />
            {messages.newsletterEyebrow}
          </p>
          <Heading
            id={`${inputId}-title`}
            className={cn(
              "font-semibold tracking-tight",
              page
                ? "mt-4 max-w-[34rem] text-4xl leading-[1.06] sm:text-5xl lg:text-[3.25rem]"
                : article
                  ? "mt-3 text-2xl leading-tight sm:text-[1.75rem]"
                  : "mt-3 text-2xl sm:text-3xl",
            )}
          >
            {messages.newsletterTitle}
          </Heading>
          {page ? (
            <ul className="mt-8 grid gap-3" aria-label={messages.newsletterDescription}>
              {pageBenefits.map(({ Icon, label }) => (
                <li
                  key={label}
                  className="flex items-center gap-3 text-sm text-slate-200 sm:text-base"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] text-sky-300">
                    <Icon aria-hidden className="h-4 w-4" />
                  </span>
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p
              className={cn(
                "mt-2 max-w-2xl text-sm leading-relaxed",
                article && "max-w-xl",
                dark ? "text-slate-300" : "text-muted-foreground",
              )}
            >
              {messages.newsletterDescription}
            </p>
          )}
        </div>

        <div
          className={cn(
            page &&
              "animate-in rounded-2xl border border-white/10 bg-white/[0.065] p-5 shadow-xl shadow-black/15 backdrop-blur-sm fade-in slide-in-from-bottom-2 fill-mode-both duration-500 [animation-delay:180ms] motion-reduce:animate-none sm:p-7",
          )}
        >
          {page && (
            <div>
              <h2
                id={formTitleId}
                className="text-xl font-semibold tracking-tight text-white sm:text-2xl"
              >
                {messages.newsletterPageFormTitle}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                {messages.newsletterPageReassurance}
              </p>
            </div>
          )}

          <form
            onSubmit={submit}
            aria-labelledby={page ? formTitleId : `${inputId}-title`}
            className={page ? "mt-6" : undefined}
          >
            <div className="sr-only" aria-hidden="true">
              <label htmlFor={`${inputId}-website`}>{messages.newsletterWebsiteLabel}</label>
              <input
                id={`${inputId}-website`}
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <label htmlFor={inputId} className="sr-only">
              {messages.newsletterEmailLabel}
            </label>
            <div className={cn("flex flex-col gap-3", !page && "sm:flex-row")}>
              <input
                id={inputId}
                name="email"
                type="email"
                required
                maxLength={200}
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (status !== "idle") setStatus("idle");
                }}
                placeholder={messages.newsletterEmailPlaceholder}
                className={cn(
                  "min-w-0 rounded-xl border px-4 text-base outline-none transition focus:ring-2 focus:ring-primary/40",
                  page ? "h-12 w-full" : "h-11 flex-1",
                  dark
                    ? "border-white/15 bg-white text-slate-950 placeholder:text-slate-500"
                    : "border-input bg-background text-foreground placeholder:text-muted-foreground",
                )}
              />
              <button
                type="submit"
                disabled={status === "submitting"}
                className={cn(
                  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60",
                  page ? "h-12 w-full" : "h-11",
                )}
              >
                {status === "submitting" ? (
                  <LoaderCircle aria-hidden className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight aria-hidden className="h-4 w-4" />
                )}
                {status === "submitting"
                  ? messages.newsletterSubmitting
                  : messages.newsletterSubmit}
              </button>
            </div>
          </form>

          <div className="mt-3 min-h-5" aria-live="polite">
            {status === "success" ? (
              <p
                className={cn(
                  "flex items-start gap-2 text-sm",
                  dark ? "text-emerald-300" : "text-emerald-700 dark:text-emerald-400",
                )}
              >
                <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <strong>{messages.newsletterSuccessTitle}.</strong>{" "}
                  {messages.newsletterSuccessBody}
                </span>
              </p>
            ) : status === "error" ? (
              <p className="text-sm text-destructive" role="alert">
                {messages.newsletterError}
              </p>
            ) : (
              <p
                className={cn(
                  "text-xs leading-relaxed",
                  page ? "text-slate-300" : dark ? "text-slate-400" : "text-muted-foreground",
                )}
              >
                {messages.newsletterPrivacyPrefix}{" "}
                <a
                  href={privacyHref}
                  className={cn(
                    "underline underline-offset-2 transition-colors",
                    dark ? "hover:text-white" : "hover:text-foreground",
                  )}
                >
                  {messages.newsletterPrivacyLink}
                </a>
                .
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (variant === "home") {
    return (
      <section aria-labelledby={`${inputId}-title`} className="bg-muted/30 px-6 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl">{panel}</div>
      </section>
    );
  }

  return (
    <section
      aria-labelledby={`${inputId}-title`}
      className={variant === "blog" ? "mt-2" : undefined}
    >
      {panel}
    </section>
  );
}
